import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  ENDPOINTS,
  IInterviewResponse,
  SUBMISSIONS_STATUSES,
  URLS,
} from '@shared/constants';
import {
  CommonResponseDTO,
  IIdentityResponse,
  ISubmissions,
  ISubmissionSentMessages,
  ISubmissionsRequest,
  ISubmissionsStatusChanges,
} from '@shared/interfaces';
import { generateURL } from '@shared/utils';

import { SCHEDULE_TYPES } from '../pages/popups/reassign-and-reschedule-interview-popup/reassign-and-reschedule-interview-popup.component';
import { ISendSubmissionData } from '../pages/popups/send-submission-popup/send-submission-popup.component';

@Injectable({
  providedIn: 'root',
})
export class SubmissionsService {
  constructor(private http: HttpClient) {}

  public createSubmission(
    consultation: ISubmissionsRequest
  ): Observable<CommonResponseDTO<ISubmissions>> {
    const formData = new FormData();
    formData.append('position', consultation.position);
    formData.append(
      'vacancyReferenceNumber',
      consultation.vacancyReferenceNumber
    );
    formData.append('first_name', consultation.first_name);
    formData.append('last_name', consultation.last_name);
    formData.append('email', consultation.email);
    formData.append('files', consultation.curriculum_vitae);
    if (consultation?.cover_letter) {
      formData.append('files', consultation.cover_letter);
    }
    if (consultation?.linked_in_profile) {
      formData.append('linked_in_profile', consultation.linked_in_profile);
    }

    Object.keys(consultation.phone_number).forEach((key) => {
      formData.append(`phone_number[${key}]`, consultation.phone_number[key]);
    });

    if (consultation.questions && consultation.questions.length > 0) {
      consultation.questions.forEach((question, index) => {
        Object.entries(question).forEach(([key, value]) => {
          if (value && Array.isArray(value)) {
            // handle arrays by appending each array element
            value.forEach((item, itemIndex) => {
              if (Array.isArray(item)) {
                // handle nested arrays
                item.forEach((nestedItem, nestedItemIndex) => {
                  formData.append(
                    `questions[${index}][${key}][${itemIndex}][${nestedItemIndex}]`,
                    nestedItem
                  );
                });
              } else if (typeof item === 'object') {
                // handle nested objects within arrays
                Object.entries(item).forEach(([nestedKey, nestedValue]) => {
                  formData.append(
                    `questions[${index}][${key}][${itemIndex}][${nestedKey}]`,
                    nestedValue as string
                  );
                });
              } else {
                // handle other types of values within arrays
                formData.append(
                  `questions[${index}][${key}][${itemIndex}]`,
                  item
                );
              }
            });
          } else if (value && typeof value === 'object') {
            // handle nested objects recursively
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
              formData.append(
                `questions[${index}][${key}][${nestedKey}]`,
                nestedValue as string
              );
            });
          } else {
            // handle other types of values
            formData.append(`questions[${index}][${key}]`, value);
          }
        });
      });
    }
    if (consultation?.referral_employee) {
      formData.append('referral_employee', consultation.referral_employee);
    }

    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSION_PUBLIC_CREATE_SUBMISSION,
    });
    return this.http.post<CommonResponseDTO<ISubmissions>>(url, formData);
  }

  // public createSubmissionPublic(
  //   consultation: ISubmissions
  // ): Observable<CommonResponseDTO<ISubmissions>> {
  //   const url = generateURL({
  //     endpoint: ENDPOINTS.SUBMISSION_PUBLIC_CREATE_SUBMISSION,
  //   });
  //   return this.http.post<CommonResponseDTO<ISubmissions>>(url, consultation);
  // }

  getAllSubmissions(
    params: HttpParams
  ): Observable<CommonResponseDTO<ISubmissions[]>> {
    const url = generateURL({ endpoint: ENDPOINTS.SUBMISSIONS_GET_ALL });
    const config = { params };
    return this.http.get<CommonResponseDTO<ISubmissions[]>>(url, config);
  }

  getUpdatedSubmission(
    params: HttpParams
  ): Observable<CommonResponseDTO<ISubmissions[]>> {
    const url = `${URLS.SUBMISSIONS}/updated-submission`;
    const config = { params };
    return this.http.get<CommonResponseDTO<ISubmissions[]>>(url, config);
  }

  getSingleSubmission(id: string): Observable<CommonResponseDTO<ISubmissions>> {
    const url = `${URLS.SUBMISSIONS}/submissions/${id}`;
    return this.http.get<CommonResponseDTO<ISubmissions>>(url, {});
  }

  updateSubmission(
    id: string,
    submission: ISubmissions
  ): Observable<CommonResponseDTO<ISubmissions>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_UPDATE,
      params: { id },
    });
    return this.http.patch<CommonResponseDTO<ISubmissions>>(url, submission);
  }

  restoreSubmission(id: string): Observable<CommonResponseDTO<ISubmissions>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_RESTORE_ONE,
      params: { id },
    });
    return this.http.patch<CommonResponseDTO<ISubmissions>>(url, {});
  }

  deleteSubmission(id: string): Observable<CommonResponseDTO<ISubmissions>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_DELETE,
      params: { id },
    });
    return this.http.delete<CommonResponseDTO<ISubmissions>>(url);
  }

  sendMessage(data: ISendSubmissionData): Observable<CommonResponseDTO<void>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_SEND_MESSAGE,
    });

    return this.http.post<CommonResponseDTO<void>>(url, data);
  }

  sendMailAndNotification(
    submission: ISubmissions,
    index: number,
    condition?: string,
    originalStatus?: string
  ): Observable<CommonResponseDTO<void>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_SEND_MAIL,
    });

    let subject = '';
    let body = '';
    let candidateMailBody = '';
    let candidateMessage = '';
    const today = new Date();
    const due_date = new Date(today.setDate(today.getDate() + 2));

    // Set the time to 11:59 PM
    due_date.setHours(23, 59, 0, 0);

    // Format the date as "DD MMM YYYY hh:mm A"
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    const formattedDate = due_date.toLocaleString('en-US', options);

    let dateObj = new Date();
    if (
      submission.status === SUBMISSIONS_STATUSES.PENDING_CONFIRMATION_TECHNICAL
    ) {
      dateObj = new Date(
        submission.technical_interview_details[
          submission.technical_interview_details.length - 1
        ].start_time
      );
    }

    if (submission.status === SUBMISSIONS_STATUSES.REQUEST_TO_RESCHEDULE) {
      dateObj = new Date(
        submission.technical_interview_details[
          submission.technical_interview_details.length - 1
        ].start_time
      );
    }

    if (submission.status === SUBMISSIONS_STATUSES.PENDING_CONFIRMATION_HR) {
      dateObj = new Date(
        submission.hr_interview_details[
          submission.hr_interview_details.length - 1
        ].start_time
      );
    }

    if (submission.status === SUBMISSIONS_STATUSES.REQUEST_TO_RESCHEDULE_HR) {
      dateObj = new Date(
        submission.hr_interview_details[
          submission.hr_interview_details.length - 1
        ].start_time
      );
    }

    const utcDate = new Date(dateObj);
    const startTime = utcDate.toLocaleString('en-US', {
      timeZone: 'Asia/Colombo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'longGeneric',
    });

    if (submission.status === SUBMISSIONS_STATUSES.SCREENING) {
      body = 'Assignment for Candidate Shortlisting Process';
      subject = `You have been assigned as a screening person for the candidate shortlisting process. Please review the candidate applications and provide your feedback by ${formattedDate}.

      <p>If you have any questions or need further information, feel free to reach out.</p>`;
      candidateMailBody = `Thank you for submitting your application for the position of ${submission.position}. We appreciate your interest in applying to our team at EchonLabs.

      Rest assured, we will review your application promptly. Only shortlisted candidates will be called for a technical interview.`;
      candidateMessage = `Your application on screening process`;
    }

    if (submission.status === SUBMISSIONS_STATUSES.REJECTED) {
      body = `${submission.position} position at EchonLabs`;
      subject = `Thank you for your interest in the ${submission.position} position at EchonLabs. Unfortunately, we will not be moving forward with your application, but we appreciate your time and interest in EchonLabs Sri Lanka.`;
      candidateMailBody = `Thank you for your interest in the ${submission.position} position at EchonLabs. Unfortunately, we will not be moving forward with your application, but we appreciate your time and interest in EchonLabs Sri Lanka.`;
      candidateMessage = `${submission.position} position at EchonLabs`;
    }

    if (submission.status === SUBMISSIONS_STATUSES.CANCELED) {
      body = `${submission.position} position at EchonLabs`;
      subject = `Thank you for your interest in the ${submission.position} position at EchonLabs. Unfortunately, we will not be moving forward with your application, but we appreciate your time and interest in EchonLabs Sri Lanka.`;
      candidateMailBody = `Thank you for your interest in the ${submission.position} position at EchonLabs. Unfortunately, we will not be moving forward with your application, but we appreciate your time and interest in EchonLabs Sri Lanka.`;
      candidateMessage = `${submission.position} position at EchonLabs`;
    }

    if (submission.status === SUBMISSIONS_STATUSES.REJECTED_WITHOUT_SCREENING) {
      body = `${submission.position} position at EchonLabs`;
      subject = `<p style="font-weight: 400; font-size: 15px">Thank you for applying for the ${submission.position} position and the interest you have shown in joining for EchonLabs.<br>
After careful consideration, we have decided not to proceed with your application due to a slight misalignment between the candidate's profile and the specific requirements of the job. We wish you the best for your professional development and career aspirations.<p>`;
      candidateMailBody = `Thank you for your interest in the ${submission.position} position at EchonLabs. Unfortunately, we will not be moving forward with your application, but we appreciate your time and interest in EchonLabs Sri Lanka.`;
      candidateMessage = `${submission.position} position at EchonLabs`;
    }

    if (submission.status === SUBMISSIONS_STATUSES.OFFER_SENT) {
      const expectedJoiningDate = new Date(
        submission?.offer_details.expectedStartDate
      ).toLocaleDateString();

      subject = `${submission.position} position at EchonLabs`;
      body = `${submission.position} position at EchonLabs`;
      candidateMailBody = `<p style="font-weight: 400; font-size: 15px">Congratulations and Warm Greetings! We are thrilled to inform you that you have been selected to join the EL family. The details of your employment are as follows:</p>

 <p style="font-weight: 650;">Designation: ${submission.offer_details.designation}<br>

Employment Type: ${submission.offer_details.employmentType}<br>

Duration: ${submission.offer_details.duration}<br>

Remuneration: ${submission.offer_details.remuneration}<br>

Mode: ${submission.offer_details.workingMode}<br>

Expected Start Date: ${expectedJoiningDate}</p>

<p style="font-weight: 400; font-size: 15px">To accept this offer, Please reply to hr@echonlabs.com. Once you accept, Please keep in mind that if you decide to resign, a minimum of 1-month notice will be required.<br>

 If you have any further questions or need clarifications, feel free to send a email to hr@echonlabs.com.</p>`;
      candidateMessage = `Offer Letter`;
    }

    if (submission.status === SUBMISSIONS_STATUSES.REJECTED_HR) {
      body = `${submission.position} position at EchonLabs`;
      subject = `Thank you for your interest in the ${submission.position} position at EchonLabs. Unfortunately, we will not be moving forward with your application, but we appreciate your time and interest in EchonLabs Sri Lanka.`;
      candidateMailBody = `Thank you for your participation at all the interview rounds. We sincerely appreciate your interest and the effort you invested in the interview process. However, after careful consideration, we must regretfully inform you that you have not been selected to proceed to the next step of our recruitment process.

We understand that this news may be disappointing, but we want to provide you with constructive feedback from our interviewers to help you in your future endeavors:

Please know that we value your potential and encourage you not to be disheartened. We believe in second chances, and if you address the areas mentioned above, we welcome you to re-apply in the future. Your determination and growth are essential to us, and we'll be more than willing to give you another opportunity when the time is right.

Wishing you all the best in your career journey.`;
      candidateMessage = `${submission.position} position at EchonLabs`;
    }

    if (submission.status === SUBMISSIONS_STATUSES.CANCELED_HR) {
      body = `${submission.position} position at EchonLabs`;
      subject = `Thank you for your interest in the ${submission.position} position at EchonLabs. Unfortunately, we will not be moving forward with your application, but we appreciate your time and interest in EchonLabs Sri Lanka.`;
      candidateMailBody = `Thank you for your participation at all the interview rounds. We sincerely appreciate your interest and the effort you invested in the interview process. However, after careful consideration, we must regretfully inform you that you have not been selected to proceed to the next step of our recruitment process.

We understand that this news may be disappointing, but we want to provide you with constructive feedback from our interviewers to help you in your future endeavors:

Please know that we value your potential and encourage you not to be disheartened. We believe in second chances, and if you address the areas mentioned above, we welcome you to re-apply in the future. Your determination and growth are essential to us, and we'll be more than willing to give you another opportunity when the time is right.

Wishing you all the best in your career journey.`;
      candidateMessage = `${submission.position} position at EchonLabs`;
    }
    if (submission.status === SUBMISSIONS_STATUSES.HR_FAILED) {
      candidateMessage = `${submission.position} position at EchonLabs`;
      body = `${submission.position} position at EchonLabs`;
      candidateMailBody = `Thank you for taking the time to attend the interviews with us as part of our selection process at Echonlabs Private Limited. We appreciate your interest in joining our team.

After careful consideration, we regret to inform you that we will not be moving forward with your application at this time. While your technical qualifications were strong, the outcome of the HR interview has led us to proceed with other candidates who more closely match our current requirements and team dynamics.

We want to sincerely thank you for the effort you put into the process and encourage you to apply for future opportunities with us that match your skills and experience.

We wish you all the best in your job search and future career endeavors.`;

      subject = `We appreciate your time during the interview process. After careful consideration, we regret to inform you that you were not selected for the next stage of the recruitment process. We thank you for your interest in EchonLabs Private Limited and encourage you to apply for future opportunities.`;
    }
    if (
      submission.status ===
        SUBMISSIONS_STATUSES.PENDING_CONFIRMATION_TECHNICAL ||
      submission.status === SUBMISSIONS_STATUSES.REQUEST_TO_RESCHEDULE
    ) {
      const interviewDate = new Date(
        submission?.technical_interview_details[
          submission.technical_interview_details.length - 1
        ].interview_date
      ).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      });

      if (
        condition === SCHEDULE_TYPES.RESCHEDULE &&
        originalStatus === SUBMISSIONS_STATUSES.PENDING_CONFIRMATION_TECHNICAL
      ) {
        if (
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ]?.meeting_type === 'Physically'
        ) {
          body = `Rescheduled: Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the Technical interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Location: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].location
        }</p>
        `;

          candidateMailBody = ` <p style="font-weight: 400; font-size: 15px">

        We are writing to let you know that your upcoming interview has been rescheduled. We sincerely apologize for any inconvenience this may cause and truly appreciate your understanding.

        <br/><br/>
        Here are the new interview details:
        <ul style="font-weight: 700; list-style-type: disc; padding-left: 0;"">
        <li>Date: ${interviewDate}<br></li>
        <li>Time: ${startTime}<br></li>
        <li>Location: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].location
        }</li>
        </p>

        <p style="font-weight: 400; font-size: 15px">Please confirm your availability for this updated schedule by clicking the "Take Action" button. We look forward to seeing you at the interview and best of luck!.<p>

        <p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

        <p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>.<p>`;

          candidateMessage = `Interview Rescheduling Notice – EchonLabs`;
        } else {
          body = `Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the Technical interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Meeting Link: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].meeting_link
        }</p>
        `;

          candidateMailBody = ` <p style="font-weight: 400; font-size: 15px">Greetings from EchonLabs!<br/><br/>

        We are writing to let you know that your upcoming interview has been rescheduled. We sincerely apologize for any inconvenience this may cause and truly appreciate your understanding.

        <br/><br/>
        Here are the new interview details:
        <ul style="font-weight: 700; list-style-type: disc; padding-left: 0;"">
        <li>Date: ${interviewDate}<br></li>
        <li>Time: ${startTime}<br></li>
        <li>Meeting link: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].meeting_link
        }</li>
        </p>

        <p style="font-weight: 400; font-size: 15px">Please confirm your availability for this updated schedule by clicking the "Take Action" button. We look forward to seeing you at the interview and best of luck!.<p>

        <p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

        <p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>.<p>`;

          candidateMessage = `Interview Rescheduling Notice – EchonLabs`;
        }
      } else if (
        condition === SCHEDULE_TYPES.RESCHEDULE &&
        originalStatus === SUBMISSIONS_STATUSES.REQUEST_TO_RESCHEDULE
      ) {
        if (
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ]?.meeting_type === 'Physically'
        ) {
          body = `Rescheduled: Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the Technical interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Location: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].location
        }</p>
        `;

          candidateMailBody = ` <p style="font-weight: 400; font-size: 15px">

        We are writing to let you know that your upcoming interview has been rescheduled as per your request.

        <br/><br/>
        Here are the new interview details:
        <ul style="font-weight: 700; list-style-type: disc; padding-left: 0;"">
        <li>Date: ${interviewDate}<br></li>
        <li>Time: ${startTime}<br></li>
        <li>Location: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].location
        }</li>
        </p>

        <p style="font-weight: 400; font-size: 15px">Please confirm your availability for this updated schedule by clicking the "Take Action" button. We look forward to seeing you at the interview and best of luck!.<p>

        <p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

        <p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>.<p>`;

          candidateMessage = `Interview Rescheduling Notice – EchonLabs`;
        } else {
          body = `Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the Technical interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Meeting Link: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].meeting_link
        }</p>
        `;

          candidateMailBody = ` <p style="font-weight: 400; font-size: 15px">Greetings from EchonLabs!<br/><br/>

        We are writing to let you know that your upcoming interview has been rescheduled as per your request.

        <br/><br/>
        Here are the new interview details:
        <ul style="font-weight: 700; list-style-type: disc; padding-left: 0;"">
        <li>Date: ${interviewDate}<br></li>
        <li>Time: ${startTime}<br></li>
        <li>Meeting link: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].meeting_link
        }</li>
        </p>

        <p style="font-weight: 400; font-size: 15px">Please confirm your availability for this updated schedule by clicking the "Take Action" button. We look forward to seeing you at the interview and best of luck!.<p>

        <p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

        <p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>.<p>`;

          candidateMessage = `Interview Rescheduling Notice – EchonLabs`;
        }
      } else {
        if (
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ]?.meeting_type === 'Physically'
        ) {
          body = `Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the Technical interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Location: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].location
        }</p>
        `;

          candidateMailBody = ` <p style="font-weight: 400; font-size: 15px">Greetings from EchonLabs!<br/><br/>

        We are pleased to inform you that you have been selected to move forward in our hiring process for the ${
          submission.position
        } role at EchonLabs. We were impressed with your background and experience and would like to invite you to a technical interview.
        <br/><br/>
        Interview Details:
        <ul style="font-weight: 700; list-style-type: disc; padding-left: 0;"">
        <li>Date: ${interviewDate}<br></li>
        <li>Time: ${startTime}<br></li>
        <li>Location: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].location
        }</li>
        </p>

        <p style="font-weight: 400; font-size: 15px">During this round, we will thoroughly evaluate your theoretical as well as practical technical skills. We kindly request you to prepare diligently to showcase your abilities. Please confirm your participation in the interview by clicking the "Take Action" button. We look forward to seeing you at the interview and wish you the best of luck!.<p>

        <p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

        <p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>.<p>`;

          candidateMessage = `${submission.position} position at EchonLabs`;
        } else {
          body = `Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the Technical interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Meeting Link: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].meeting_link
        }</p>
        `;

          candidateMailBody = ` <p style="font-weight: 400; font-size: 15px">Greetings from EchonLabs!<br/><br/>

        We are pleased to inform you that you have been selected to move forward in our hiring process for the ${
          submission.position
        } role at EchonLabs. We were impressed with your background and experience and would like to invite you to a technical interview.
        <br/><br/>
        Interview Details:
        <ul style="font-weight: 700; list-style-type: disc; padding-left: 0;"">
        <li>Date: ${interviewDate}<br></li>
        <li>Time: ${startTime}<br></li>
        <li>Meeting link: ${
          submission.technical_interview_details[
            submission.technical_interview_details.length - 1
          ].meeting_link
        }</li>
        </p>

        <p style="font-weight: 400; font-size: 15px">During this round, we will thoroughly evaluate your theoretical as well as practical technical skills. We kindly request you to prepare diligently to showcase your abilities. Please confirm your participation in the interview by clicking the "Take Action" button. We look forward to seeing you at the interview and wish you the best of luck!.<p>

        <p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

        <p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>.<p>`;

          candidateMessage = `${submission.position} position at EchonLabs`;
        }
      }
    }
    if (
      submission.status === SUBMISSIONS_STATUSES.PENDING_CONFIRMATION_HR ||
      submission.status === SUBMISSIONS_STATUSES.REQUEST_TO_RESCHEDULE_HR
    ) {
      const interviewDate = new Date(
        submission?.hr_interview_details[
          submission.hr_interview_details.length - 1
        ].interview_date
      ).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      });

      if (condition === SCHEDULE_TYPES.RESCHEDULE) {
        if (
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ]?.location
        ) {
          body = `Rescheduled: Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the HR interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Location: ${
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ].location
        }</p>
        `;

          candidateMailBody = `<p style="font-weight: 400; font-size: 15px">We are writing to let you know that your upcoming interview has been rescheduled. We sincerely apologize for any inconvenience this may cause and truly appreciate your understanding.
        
          <br/><br/>
          Here are the new interview details:
          <ul style="font-weight: 700; list-style-type: disc; padding-left: 0;"">
          <li> Date: ${interviewDate}<br></li>
          <li> Time: ${startTime}<br></li>
          <li>Location: ${
            submission.hr_interview_details[
              submission.hr_interview_details.length - 1
            ].location
          }</li>
        </p>

<p style="font-weight: 400; font-size: 15px">Please confirm your availability for this updated schedule by clicking the "Take Action" button. We look forward to seeing you at the interview and best of luck!.<p>

<p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

<p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email hr@echonlabs.com.</p>`;

          candidateMessage = `Interview Rescheduling Notice – EchonLabs`;
        } else {
          body = `Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the HR interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Meeting Link: ${
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ].meeting_link
        }</p>
        `;

          candidateMailBody = `<p style="font-weight: 400; font-size: 15px">We are delighted to extend our congratulations to you! Your outstanding performance in the previous round has earned you a spot in the next and final stage of our recruitment process, the Human Resources Interview. Below are the details for this upcoming interview:<p>

  <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Meeting Link: ${
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ].meeting_link
        }</p>

  <p style="font-weight: 400; font-size: 15px">Your technical expertise has impressed us, and now we are eager to discuss your future plans and explore how we can ensure that you can make the best possible contribution to EL while receiving the utmost support and opportunities from our organization. To confirm your participation in the interview, kindly respond to this  email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>. If you have any queries or require further information, please do not hesitate to contact me. We look forward to seeing you at the interview and wish you the best of luck!<p>`;
          candidateMessage = `${submission.position} position at EchonLabs`;
        }
      } else {
        if (
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ]?.location
        ) {
          body = `Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the HR interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Location: ${
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ].location
        }</p>
        `;

          candidateMailBody = `<p style="font-weight: 400; font-size: 15px">We are delighted to extend our congratulations to you! Your outstanding performance in the previous round has earned you a spot in the next and final stage of our recruitment process, the Human Resources Interview. Below are the details for this upcoming interview:<p>

  <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Location: ${
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ].location
        }</p>

  <p style="font-weight: 400; font-size: 15px">Your technical expertise has impressed us, and now we are eager to discuss your future plans and explore how we can ensure that you can make the best possible contribution to EL while receiving the utmost support and opportunities from our organization. Please confirm your participation in the interview by clicking the "Take Action" button. We look forward to seeing you at the interview and wish you the best of luck!<p>

<p style="font-weight: 400; font-size: 15px">We look forward to your response and hope to meet you soon.</p>

<p style="font-weight: 400; font-size: 15px">If you have any further questions or need clarifications, feel free to reply to this email hr@echonlabs.com.</p>`;

          candidateMessage = `${submission.position} position at EchonLabs`;
        } else {
          body = `Interviewer for the ${submission.position} Position`;
          subject = `You have been assigned to conduct the HR interview for the ${
            submission.position
          } position with ${submission.first_name} ${
            submission.last_name
          }. Below are the details:<br>
        <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Meeting Link: ${
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ].meeting_link
        }</p>
        `;

          candidateMailBody = `<p style="font-weight: 400; font-size: 15px">We are delighted to extend our congratulations to you! Your outstanding performance in the previous round has earned you a spot in the next and final stage of our recruitment process, the Human Resources Interview. Below are the details for this upcoming interview:<p>

  <p style="font-weight: 700;">Date: ${interviewDate}<br>
        Time: ${startTime}<br>
        Meeting Link: ${
          submission.hr_interview_details[
            submission.hr_interview_details.length - 1
          ].meeting_link
        }</p>

  <p style="font-weight: 400; font-size: 15px">Your technical expertise has impressed us, and now we are eager to discuss your future plans and explore how we can ensure that you can make the best possible contribution to EL while receiving the utmost support and opportunities from our organization. To confirm your participation in the interview, kindly respond to this  email <a href="mailto:hr@echonlabs.com">hr@echonlabs.com</a>. If you have any queries or require further information, please do not hesitate to contact me. We look forward to seeing you at the interview and wish you the best of luck!<p>`;
          candidateMessage = `${submission.position} position at EchonLabs`;
        }
      }
    }

    const mailData = {
      submission,
      index,
      subject,
      body,
      candidateMailBody,
      candidateMessage,
      condition,
    };

    return this.http.post<CommonResponseDTO<void>>(url, mailData);
  }

  getStatusChanges(
    id: string
  ): Observable<CommonResponseDTO<ISubmissionsStatusChanges[]>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_STATUS_CHANGE,
      params: { id },
    });

    return this.http.get<CommonResponseDTO<ISubmissionsStatusChanges[]>>(url);
  }

  getSameMailCount(mail: string): Observable<CommonResponseDTO<number>> {
    const url = `${URLS.SUBMISSIONS}/duplicates/email`;
    const params = new HttpParams().set('mail', mail);
    return this.http.get<CommonResponseDTO<number>>(url, { params });
  }

  findEmailDuplicates(mail: string, position: string) {
    const url = `${URLS.PUBLIC_SUBMISSIONS}/previous-submissions`;
    const params = new HttpParams().set('mail', mail).set('position', position);

    return this.http.get<CommonResponseDTO<number>>(url, { params });
  }

  getSentMessages(
    id: string
  ): Observable<CommonResponseDTO<ISubmissionSentMessages[]>> {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_SENT_MESSAGES,
      params: { id },
    });

    return this.http.get<CommonResponseDTO<ISubmissionSentMessages[]>>(url);
  }

  updateSubmissionResponse(id: string, formData: IInterviewResponse) {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_PUBLIC_GET_RESPONSE,
      params: { id },
    });

    return this.http.patch<CommonResponseDTO<ISubmissions>>(url, formData);
  }

  updateSubmissionResponseHR(id: string, formData: IInterviewResponse) {
    const url = generateURL({
      endpoint: ENDPOINTS.SUBMISSIONS_PUBLIC_GET_RESPONSE_HR,
      params: { id },
    });

    return this.http.patch<CommonResponseDTO<ISubmissions>>(url, formData);
  }

  getPublicSingleSubmission(
    id: string
  ): Observable<CommonResponseDTO<ISubmissions>> {
    const url = `${URLS.PUBLIC_SUBMISSIONS}/submissions/${id}`;
    return this.http.get<CommonResponseDTO<ISubmissions>>(url, {});
  }

  public getIdentitiesWithoutContactPerson(
    params: HttpParams
  ): Observable<CommonResponseDTO<IIdentityResponse[]>> {
    const url = `${URLS.IDENTITIES_V2}/async-filter`;
    const config = { params };
    return this.http.get<CommonResponseDTO<IIdentityResponse[]>>(url, config);
  }
}
